import {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import useLiterals from 'utils/hooks/useLiterals';
import {
  getUserData,
  processPayment,
  getPaymentStatus,
  processMultiplePayment,
  saveCard,
  deleteCard,
} from 'modules/mercadopago/actions';
import { paymentTypeIdToCardType } from 'utils/mercadopago';
import MercadoPagoApi from 'modules/mercadopago/repository';
import { cleanupAll } from 'utils/mercadopagoFields';
import { STEPS, CHALLENGE_POLLING } from 'constants/mercadopago';
import Debugger from 'utils/debugger';
import { captureMercadoPagoError } from 'utils/sendSentryLog';
import { toast } from 'react-toastify';

/**
 * Hook que concentra todo el estado y la lógica del flujo Mercado Pago.
 * Usa STEPS y CHALLENGE_POLLING; los mensajes de error salen de literals.errors.
 * @param {object} props - amount, type, id, payment, orders, isMultiple, onClose, onSuccess, onPending
 * @returns {object} - Estado y handlers para el componente MercadoPago
 */
export function useMercadoPagoFlow({
  amount = 0,
  type = '',
  id = '',
  payment,
  orders = [],
  isMultiple = false,
  coverage = 0,
  coverageId = null,
  onClose = () => {},
  onSuccess = () => {},
  onPending = null,
}) {
  const literals = useLiterals('mercadopago');
  const { user } = useSelector((state) => state.session);

  const [step, setStep] = useState(STEPS.LOADING);
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [editingCard, setEditingCard] = useState(null);
  const [showCvvModal, setShowCvvModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [deletingCard, setDeletingCard] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCards, setLoadingCards] = useState(true);
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);
  const [success, setSuccess] = useState(false);
  const [challengeData, setChallengeData] = useState(null);
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  const successResponseRef = useRef(null);

  // Igual que BBVA: amount = capital, monto = payment.amount; total a cobrar = amount + coverage
  const totalAmount = isMultiple ? amount : (amount || 0) + (coverage || 0);

  const goToSummaryWithError = useCallback((errorMessage, clearChallenge = false) => {
    setConsecutiveErrors((c) => c + 1);
    cleanupAll().finally(() => {
      setLoading(false);
      setWarning(null);
      setError(errorMessage);
      setStep(STEPS.SUMMARY);
      if (clearChallenge) setChallengeData(null);
    });
  }, []);

  const goToSummaryWithWarning = useCallback((warningMessage, clearChallenge = false) => {
    cleanupAll().finally(() => {
      setLoading(false);
      setError(null);
      setWarning(warningMessage);
      setStep(STEPS.SUMMARY);
      if (clearChallenge) setChallengeData(null);
    });
  }, []);

  const stepRef = useRef(step);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  const loadCards = useCallback(async (preserveStep = false) => {
    if (!user?.email || !user?.id) {
      setLoadingCards(false);
      if (!preserveStep) {
        setStep(STEPS.FORM);
      }
      return;
    }
    setLoadingCards(true);
    try {
      const response = await getUserData(user.email, user.id);
      if (response?.cards?.length > 0) {
        setCards(response.cards);
        if (!preserveStep && stepRef.current === STEPS.LOADING) {
          setStep(STEPS.CARDS);
        }
      } else {
        setCards([]);
        if (!preserveStep && stepRef.current === STEPS.LOADING) {
          setStep(STEPS.FORM);
        }
      }
    } catch (err) {
      Debugger.error('Error al cargar tarjetas:', err);
      captureMercadoPagoError(err, {
        feature: 'useMercadoPagoFlow',
        step: 'loadCards',
        userId: user?.id,
        email: user?.email,
      });
      setCards([]);
      if (!preserveStep && stepRef.current === STEPS.LOADING) {
        setStep(STEPS.FORM);
      }
    } finally {
      setLoadingCards(false);
    }
  }, [user?.email, user?.id]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  const handleCardSelect = useCallback((card) => {
    if (!card.disabled) {
      setSelectedCard(card);
      setShowCvvModal(true);
    }
  }, []);

  const handleAddNewCard = useCallback(() => {
    setStep(STEPS.FORM);
    setSelectedCard(null);
  }, []);

  const handleCvvSubmit = useCallback(async (data) => {
    setShowCvvModal(false);
    setLoading(true);
    setError(null);
    const paymentInfo = {
      token: data.token || selectedCard?.token || '',
      amount: totalAmount,
      paymentMethodId: data.paymentMethodId || selectedCard?.payment_method?.id || '',
      email: user?.email || '',
      installments: data.installments || '1',
      cardholderName: data.cardholderName || selectedCard?.cardholder?.name || '',
      cardNumber: data.cardNumber || '',
      last_four_digits: data.last_four_digits || selectedCard?.last_four_digits || '',
      cardType: paymentTypeIdToCardType(
        data.payment_type_id
          || selectedCard?.payment_type_id
          || selectedCard?.payment_method?.payment_type_id,
      ),
    };
    setPaymentData(paymentInfo);
    setStep(STEPS.SUMMARY);
    setLoading(false);
  }, [selectedCard, totalAmount, user?.email]);

  const handlePaymentReady = useCallback((data) => {
    setPaymentData({ ...data, email: user?.email || '' });
    setStep(STEPS.SUMMARY);
  }, [user?.email]);

  const handleConfirmPayment = useCallback(async () => {
    if (loading) return;
    setStep(STEPS.PROCESSING);
    setLoading(true);
    setError(null);
    setWarning(null);

    try {
      // Igual que BBVA: amount = capital, monto = concepto (payment.amount)
      const orderData = {
        amount: isMultiple ? paymentData.amount : amount,
        monto: payment?.amount,
        paymentMethodId: paymentData.paymentMethodId,
        email: paymentData.email,
        installments: Number(paymentData.installments) || 1,
        issuer: paymentData.issuer || '',
        cardNumber: paymentData.cardNumber || '',
        token: paymentData.token || '',
        cardType: paymentData.cardType || '',
        cardholderName: paymentData.cardholderName || '',
        contractType: type,
        id,
        paymentKey: payment?.paymentKey || null,
        paymentName: payment?.title || payment?.paymentName || '',
        coverage: coverage || 0,
        coverageId: coverageId || null,
        externalReference: `EXT-${Date.now()}-${user?.id || ''}`,
      };

      let response;
      if (isMultiple) {
        const payload = {
          totalAmount: amount,
          orders,
          os: window.getOS() || 'WEB',
          paymentMetadata: {
            token: paymentData.token || '',
            cardNumber: `************${paymentData.last_four_digits}`,
            cardType: paymentData.cardType || '',
            externalOrder: orderData.externalReference,
            paymentDescription: `Pago múltiple de ${amount} - ${paymentData.cardholderName || paymentData.email || 'Usuario'}`,
            paymentMethodId: paymentData.paymentMethodId || '',
          },
        };
        response = await processMultiplePayment(payload);
      } else {
        response = await processPayment(orderData);
      }

      setLoading(false);
      const paymentResponseData = response?.data || response;
      if (paymentResponseData) {
        const status = paymentResponseData.status;
        const statusDetail = paymentResponseData.status_detail ?? paymentResponseData.statusDetail;
        const threeDsInfo = paymentResponseData.three_ds_info
          ?? paymentResponseData.threeDsInfo
          ?? paymentResponseData.threeDS;

        if (status === 'pending' && statusDetail === 'pending_challenge' && threeDsInfo) {
          const hasCreq = threeDsInfo.creq && String(threeDsInfo.creq).trim() !== '';
          const hasUrl = threeDsInfo.external_resource_url || threeDsInfo.challengeUrl;
          if (!hasCreq || !hasUrl) {
            const err = new Error('Para completar la autenticación 3DS la respuesta del pago debe incluir el campo creq (en three_ds_info o threeDS). Revisa que tu backend devuelva la respuesta completa de la API de MercadoPago.');
            captureMercadoPagoError(err, {
              feature: 'useMercadoPagoFlow',
              step: 'missingCreq',
              userId: user?.id,
              email: user?.email,
              paymentId: paymentResponseData.id,
            });
            goToSummaryWithError(literals.errors?.challengeError);
            return;
          }
          setChallengeData({ paymentId: paymentResponseData.id, threeDsInfo });
          setStep(STEPS.CHALLENGE);
          return;
        }

        if (status === 'approved') {
          setConsecutiveErrors(0);
          try {
            if (paymentData.saveCard) {
              const paymentTypeId = paymentData.cardType === 'D'
                ? 'debit_card'
                : 'credit_card';
              await MercadoPagoApi.saveCard({
                userId: user?.id || '',
                token: paymentData?.token || '',
                payment_type_id: paymentTypeId,
              });
            }
          } catch (err) {
            Debugger.error('Error al guardar la tarjeta:', err);
            captureMercadoPagoError(err, {
              feature: 'useMercadoPagoFlow',
              step: 'handleConfirmPayment_saveCard',
              userId: user?.id,
              email: user?.email,
            });
            setError(literals.errors?.saveCardFailed || 'Error al guardar la tarjeta');
          }
          successResponseRef.current = response;
          onSuccess?.(response);
          onClose?.();
          return;
        }

        if (status === 'rejected') {
          const statusDetails = literals.statusDetails || {};
          const msg = statusDetail === 'cc_rejected_3ds_challenge'
            ? literals.errors?.rejected3ds
            : (statusDetail && statusDetails[statusDetail]) || literals.errors?.rejected || statusDetail;
          goToSummaryWithError(msg);
          return;
        }

        if (status === 'cancelled' || statusDetail === 'expired') {
          goToSummaryWithError(literals.errors?.expired);
          return;
        }

        if (status === 'pending') {
          const pendingMsg = (literals.statusDetails && statusDetail && literals.statusDetails[statusDetail])
            || literals.errors?.pending;
          if (onPending) {
            onPending({
              status,
              statusDetail,
              message: pendingMsg,
              paymentId: paymentResponseData.id,
              empaques: paymentResponseData.empaques,
            });
            onClose?.();
            return;
          }
          goToSummaryWithWarning(pendingMsg);
          return;
        }

        if (status === 'in_process' || status === 'authorized') {
          // in_process / authorized: mensaje informativo como warning (no error)
          const statusDetails = literals.statusDetails || {};
          const msg = (statusDetail && statusDetails[statusDetail]) || literals.errors?.paymentFailed;
          if (onPending) {
            onPending({
              status,
              statusDetail,
              message: msg,
              paymentId: paymentResponseData.id,
              empaques: paymentResponseData.empaques,
            });
            onClose?.();
            return;
          }
          goToSummaryWithWarning(msg);
          return;
        }

        // Cualquier otro status no aprobado
        const statusDetails = literals.statusDetails || {};
        const msg = (statusDetail && statusDetails[statusDetail]) || literals.errors?.paymentFailed;
        goToSummaryWithError(msg);
        return;
      }

      if (!response?.success) {
        goToSummaryWithError(literals.summary?.errorMessage || literals.errors?.paymentFailed);
      }
    } catch (err) {
      captureMercadoPagoError(err, {
        feature: 'useMercadoPagoFlow',
        step: 'handleConfirmPayment',
        userId: user?.id,
        email: user?.email,
      });
      goToSummaryWithError(literals.summary?.errorMessage || literals.errors?.paymentFailed);
    }
  }, [
    loading,
    paymentData,
    amount,
    type,
    id,
    payment,
    orders,
    isMultiple,
    coverage,
    coverageId,
    user?.id,
    user?.email,
    literals.errors,
    literals.statusDetails,
    literals.summary?.errorMessage,
    goToSummaryWithError,
    goToSummaryWithWarning,
    onPending,
    onSuccess,
    onClose,
  ]);

  const handleBack = useCallback(() => {
    cleanupAll()
      .then(() => {
        setStep(cards.length > 0 ? STEPS.CARDS : STEPS.FORM);
        setPaymentData(null);
        setSelectedCard(null);
        setChallengeData(null);
        setError(null);
        setWarning(null);
      })
      .catch(() => {
        setStep(cards.length > 0 ? STEPS.CARDS : STEPS.FORM);
        setPaymentData(null);
        setSelectedCard(null);
        setChallengeData(null);
      });
  }, [cards.length]);

  const handleChallengeComplete = useCallback(async () => {
    if (!challengeData?.paymentId) {
      goToSummaryWithError(literals.errors?.verificationFailed, true);
      return;
    }
    setLoading(true);
    const { MAX_ATTEMPTS, DELAY_MS } = CHALLENGE_POLLING;
    let attempts = 0;

    const checkPaymentStatus = async () => {
      try {
        const statusResponse = await getPaymentStatus(challengeData.paymentId);
        const data = statusResponse?.data ?? statusResponse;
        const status = data?.status;
        const statusDetail = data?.status_detail ?? data?.statusDetail;
        const threeDsInfo = data?.three_ds_info ?? data?.threeDsInfo ?? data?.threeDS;
        const isSuccess = statusResponse?.success === true || status === 'approved';

        if (isSuccess && status === 'approved') {
          setConsecutiveErrors(0);
          setLoading(false);
          setChallengeData(null);
          try {
            if (paymentData?.saveCard) {
              const paymentTypeId = paymentData.cardType === 'D'
                ? 'debit_card'
                : 'credit_card';
              await MercadoPagoApi.saveCard({
                userId: user?.id || '',
                token: paymentData?.token || '',
                payment_type_id: paymentTypeId,
              });
            }
          } catch (err) {
            Debugger.error('Error al guardar la tarjeta tras challenge:', err);
            captureMercadoPagoError(err, {
              feature: 'useMercadoPagoFlow',
              step: 'handleChallengeComplete_saveCard',
              userId: user?.id,
              email: user?.email,
              paymentId: challengeData?.paymentId,
            });
            setError(literals.errors?.saveCardFailed || 'Error al guardar la tarjeta');
          }
          successResponseRef.current = statusResponse;
          onSuccess?.(statusResponse);
          onClose?.();
          return;
        }

        // Resto de status (rejected, pending, etc.) cuando la API devuelve data con status
        if (data != null && status != null) {
          // Re-mostrar challenge solo si threeDsInfo tiene creq y URL; si viene creq/challengeUrl null (challenge ya completado), ir al resumen
          const hasValidChallenge = threeDsInfo
            && threeDsInfo.creq
            && (threeDsInfo.external_resource_url || threeDsInfo.challengeUrl);
          if (status === 'pending' && statusDetail === 'pending_challenge' && hasValidChallenge) {
            setLoading(false);
            setChallengeData({ paymentId: data?.id || challengeData.paymentId, threeDsInfo });
            setStep(STEPS.CHALLENGE);
            return;
          }
          if (status === 'rejected') {
            const statusDetails = literals.statusDetails || {};
            const msg = statusDetail === 'cc_rejected_3ds_challenge'
              ? literals.errors?.rejected3ds
              : (statusDetail && statusDetails[statusDetail]) || literals.errors?.rejected || statusDetail;
            goToSummaryWithError(msg, true);
            return;
          }
          if (status === 'cancelled' || statusDetail === 'expired') {
            goToSummaryWithError(literals.errors?.expired, true);
            return;
          }
          if (status === 'in_process' || status === 'authorized') {
            setLoading(false);
            const statusDetails = literals.statusDetails || {};
            const msg = (statusDetail && statusDetails[statusDetail]) || literals.errors?.paymentFailed;
            if (onPending) {
              onPending({
                status,
                statusDetail,
                message: msg,
                paymentId: data?.id,
                empaques: data?.empaques,
              });
              onClose?.();
              return;
            }
            goToSummaryWithWarning(msg, true);
            return;
          }
          // pending (tras el challenge): ir al resumen y avisar que el pago está pendiente; si marcó guardar tarjeta, guardarla
          if (status === 'pending') {
            setLoading(false);
            setChallengeData(null);
            try {
              if (paymentData?.saveCard) {
                const paymentTypeId = paymentData.cardType === 'D'
                  ? 'debit_card'
                  : 'credit_card';
                await MercadoPagoApi.saveCard({
                  userId: user?.id || '',
                  token: paymentData?.token || '',
                  payment_type_id: paymentTypeId,
                });
              }
            } catch (err) {
              Debugger.error('Error al guardar la tarjeta tras challenge (pending):', err);
              captureMercadoPagoError(err, {
                feature: 'useMercadoPagoFlow',
                step: 'handleChallengeComplete_saveCard_pending',
                userId: user?.id,
                email: user?.email,
                paymentId: challengeData?.paymentId,
              });
              setError(literals.errors?.saveCardFailed || 'Error al guardar la tarjeta');
            }
            const pendingMsg = (literals.statusDetails && statusDetail && literals.statusDetails[statusDetail])
              || literals.errors?.pending
              || literals.errors?.paymentPendingMax;
            if (onPending) {
              onPending({
                status: 'pending',
                statusDetail,
                message: pendingMsg,
                paymentId: data?.id || challengeData.paymentId,
                empaques: data?.empaques,
              });
              onClose?.();
              return;
            }
            goToSummaryWithWarning(pendingMsg, true);
            return;
          }
          // Cualquier otro status no contemplado: ir al resumen con mensaje
          setLoading(false);
          const statusDetails = literals.statusDetails || {};
          const msg = (statusDetail && statusDetails[statusDetail]) || literals.errors?.paymentFailed;
          if (onPending) {
            onPending({
              status,
              statusDetail,
              message: msg,
              paymentId: data?.id,
              empaques: data?.empaques,
            });
            onClose?.();
            return;
          }
          goToSummaryWithWarning(msg, true);
          return;
        }

        if (attempts < MAX_ATTEMPTS) {
          attempts += 1;
          setTimeout(checkPaymentStatus, DELAY_MS);
        } else {
          goToSummaryWithError(literals.errors?.verificationError, true);
        }
      } catch (err) {
        Debugger.error('Error al verificar estado del pago:', err);
        captureMercadoPagoError(err, {
          feature: 'useMercadoPagoFlow',
          step: 'handleChallengeComplete',
          userId: user?.id,
          email: user?.email,
          paymentId: challengeData?.paymentId,
        });
        if (attempts < MAX_ATTEMPTS) {
          attempts += 1;
          setTimeout(checkPaymentStatus, DELAY_MS);
        } else {
          goToSummaryWithError(literals.errors?.verificationError, true);
        }
      }
    };
    checkPaymentStatus();
  }, [
    challengeData,
    paymentData,
    user?.id,

    user?.email,
    literals.errors,
    literals.statusDetails,
    onPending,
    onSuccess,
    onClose,
    goToSummaryWithError,
    goToSummaryWithWarning,
  ]);

  const handleChallengeError = useCallback((challengeError) => {
    Debugger.error('Error en Challenge 3DS:', challengeError);
    captureMercadoPagoError(challengeError, {
      feature: 'useMercadoPagoFlow',
      step: 'handleChallengeError',
      userId: user?.id,
      email: user?.email,
      paymentId: challengeData?.paymentId,
    });
    goToSummaryWithError(literals.errors?.challengeError, true);
  }, [literals.errors?.challengeError, goToSummaryWithError, user?.id, user?.email, challengeData?.paymentId]);

  const handleClose = useCallback(() => {
    setConsecutiveErrors(0);
    onClose?.();
  }, [onClose]);

  const handleUseOtherPaymentMethod = useCallback(() => {
    setConsecutiveErrors(0);
    onClose?.();
  }, [onClose]);

  /** Llamar cuando el usuario cierra el modal de pago completado; redirige/navega entonces. */
  const handleSuccessClose = useCallback(() => {
    setSuccess(false);
    const res = successResponseRef.current;
    successResponseRef.current = null;
    onSuccess?.(res);
    onClose?.();
  }, [onSuccess, onClose]);

  const setStepFormOrCards = useCallback(() => {
    setStep(cards.length > 0 ? STEPS.CARDS : STEPS.FORM);
  }, [cards.length]);

  const handleEditCard = useCallback((card) => {
    setEditingCard(card);
    setStep(STEPS.EDIT_CARD);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingCard(null);
    setStep(cards.length > 0 ? STEPS.CARDS : STEPS.FORM);
  }, [cards.length]);

  const handleDeleteCard = useCallback((card) => {
    setCardToDelete(card);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!cardToDelete || !user?.id || deletingCard) {
      return;
    }

    setDeletingCard(true);
    try {
      const response = await deleteCard(user.id, cardToDelete.id);
      if (response.success) {
        setShowDeleteModal(false);
        setCardToDelete(null);
        // Recargar tarjetas preservando el step actual
        await loadCards(true);
        // Mostrar notificación de éxito (auto-dismiss)
        toast.success(literals.deleteCardModal?.successMessage || literals.cardDeleted || 'Tarjeta eliminada correctamente', {
          autoClose: 3000,
        });
        // Si estábamos editando la tarjeta que se eliminó, volver a la lista
        if (editingCard?.id === cardToDelete.id) {
          setEditingCard(null);
          setStep(cards.length > 1 ? STEPS.CARDS : STEPS.FORM);
        }
      } else {
        const errorMessage = literals.errors?.deleteCardFailed || 'Error al eliminar la tarjeta';
        setError(errorMessage);
        toast.error(errorMessage, {
          autoClose: 4000,
        });
      }
    } catch (err) {
      Debugger.error('Error al eliminar tarjeta:', err);
      captureMercadoPagoError(err, {
        feature: 'useMercadoPagoFlow',
        step: 'handleConfirmDelete',
        userId: user?.id,
        email: user?.email,
      });
      const errorMessage = literals.errors?.deleteCardFailed || 'Error al eliminar la tarjeta';
      setError(errorMessage);
      toast.error(errorMessage, {
        autoClose: 4000,
      });
    } finally {
      setDeletingCard(false);
    }
  }, [cardToDelete, user?.id, user?.email, deletingCard, editingCard, cards.length, literals, loadCards]);

  const handleUpdateCard = useCallback(async (cardData) => {
    if (!editingCard || !user?.id || loading) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Usar saveCard en lugar de updateCard para actualizar la tarjeta
      // saveCard lanza error si falla, si llega aquí es exitoso
      await saveCard(user.id, cardData.token);
      // Recargar tarjetas preservando el step actual (no cambiar a LOADING)
      await loadCards(true);
      // Mostrar notificación de éxito (auto-dismiss)
      toast.success(literals.editCardForm?.successMessage || literals.cardUpdated || 'Tarjeta actualizada correctamente', {
        autoClose: 3000,
      });
      setEditingCard(null);
      setStep(STEPS.CARDS);
    } catch (err) {
      Debugger.error('Error al actualizar tarjeta:', err);
      captureMercadoPagoError(err, {
        feature: 'useMercadoPagoFlow',
        step: 'handleUpdateCard',
        userId: user?.id,
        email: user?.email,
      });
      const errorMessage = literals.errors?.updateCardFailed || 'Error al actualizar la tarjeta';
      setError(errorMessage);
      toast.error(errorMessage, {
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, [editingCard, user?.id, user?.email, loading, literals, loadCards]);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setCardToDelete(null);
  }, []);

  return {
    literals,
    step,
    cards,
    selectedCard,
    editingCard,
    showCvvModal,
    showDeleteModal,
    cardToDelete,
    deletingCard,
    setShowCvvModal,
    paymentData,
    loading,
    loadingCards,
    error,
    warning,
    success,
    setSuccess,
    challengeData,
    recommendOtherMethod: consecutiveErrors >= 3,
    handleUseOtherPaymentMethod,
    user,
    handleCardSelect,
    handleAddNewCard,
    handleEditCard,
    handleCancelEdit,
    handleDeleteCard,
    handleConfirmDelete,
    handleCloseDeleteModal,
    handleUpdateCard,
    handleCvvSubmit,
    handlePaymentReady,
    handleConfirmPayment,
    handleBack,
    handleChallengeComplete,
    handleChallengeError,
    handleClose,
    handleSuccessClose,
    setStepFormOrCards,
  };
}
